<template>
  <!-- 销售合同统计：合同类型结构、金额趋势与明细 -->
  <report-layout active="contract">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="contractReport">
        <template #default>
          <yq-date-range v-model:start-time="query.startTime" v-model:end-time="query.endTime" class="double-width">
            <template #prefix>签约日期</template>
          </yq-date-range>
          <el-select v-model="query.contractType" placeholder="全部合同类型" clearable>
            <template #prefix>合同类型</template>
            <el-option v-for="(label, value) in contractTypeMap" :key="value" :label="label" :value="Number(value)"/>
          </el-select>
          <employee-select v-model="query.salesId" placeholder="全部" clearable>
            <template #prefix>负责人</template>
          </employee-select>
        </template>
        <template #option>
          <page-button @click="resetAll">重置</page-button>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
        </template>
      </search-condition>

      <list-table :show-shadow="false">
        <div v-loading="loading" class="report-body">
          <div class="stat-grid">
            <div class="stat-card">
              <div class="label">合同数量</div>
              <div class="value">{{ stat.totalCount || 0 }}<span class="unit">份</span></div>
              <div class="sub">筛选条件下的全部合同</div>
            </div>
            <div class="stat-card">
              <div class="label">合同金额</div>
              <div class="value money">{{ money(stat.totalAmount) }}<span class="unit">元</span></div>
            </div>
            <div class="stat-card">
              <div class="label">已回款金额</div>
              <div class="value money success">{{ money(stat.receivedAmount) }}<span class="unit">元</span></div>
            </div>
            <div class="stat-card">
              <div class="label">回款率</div>
              <div class="value">{{ stat.receiptRate || 0 }}<span class="unit">%</span></div>
            </div>
          </div>

          <div class="grid-2">
            <div class="chart-card">
              <div class="section-title">合同类型分布</div>
              <div class="donut-wrap">
                <div ref="typeChart" class="donut-div"></div>
                <div class="donut-legend">
                  <div v-for="row in typeRows" :key="row.name" class="lg-row">
                    <span class="lg-dot" :style="{background: row.color}"></span>
                    <span class="lg-name">{{ row.name }}</span>
                    <span class="lg-val">{{ row.count }}</span>
                    <span class="lg-ratio">{{ row.ratio }}%</span>
                  </div>
                  <div v-if="!typeRows.length" class="lg-empty">暂无数据</div>
                </div>
              </div>
            </div>
            <div class="chart-card">
              <div class="section-title">合同金额趋势</div>
              <div ref="trendChart" class="chart-div"></div>
            </div>
          </div>

          <div class="chart-card">
            <div class="section-title">统计明细列表</div>
            <el-table :data="typeRows" border size="small" show-summary :summary-method="summaryMethod" empty-text="暂无数据">
              <el-table-column label="序号" width="70" align="center">
                <template v-slot="{$index}">{{ $index + 1 }}</template>
              </el-table-column>
              <el-table-column label="合同类型" prop="name">
                <template v-slot="{row}">
                  <span class="lg-dot inline" :style="{background: row.color}"></span>{{ row.name }}
                </template>
              </el-table-column>
              <el-table-column label="合同数量" prop="count" align="right" width="120">
                <template v-slot="{row}"><span class="num bold">{{ row.count }}</span></template>
              </el-table-column>
              <el-table-column label="合同金额" prop="amount" align="right" width="180">
                <template v-slot="{row}"><span class="num">{{ money(row.amount) }}</span></template>
              </el-table-column>
              <el-table-column label="金额占比" prop="amountRatio" align="right" width="140">
                <template v-slot="{row}"><span class="num">{{ row.amountRatio }}%</span></template>
              </el-table-column>
              <el-table-column label="数量占比" prop="ratio" align="right" width="140">
                <template v-slot="{row}"><span class="num">{{ row.ratio }}%</span></template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </list-table>
    </list-page>
  </report-layout>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ReportLayout from "@/views/h5/crm/report/ReportLayout.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqDateRange from "@/components/h5/date/YqDateRange.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import contractApi from "@/api/dz-crm/partner/contractApi";
import {CONTRACT_TYPE_MAP} from "@/views/h5/crm/contract/contractDictionary";
import {REPORT_PALETTE, cleanParams, donutOption, lineOption, money} from "@/views/h5/crm/report/reportUtil";

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default {
  name: "ContractReport",
  components: {ReportLayout, ListPage, ListTable, SearchCondition, PageButton, YqDateRange, EmployeeSelect},
  data() {
    const year = new Date().getFullYear();
    return {
      contractTypeMap: CONTRACT_TYPE_MAP,
      loading: false,
      query: {startTime: `${year}-01-01`, endTime: today(), contractType: "", salesId: ""},
      stat: {},
      typeChart: null,
      trendChart: null,
    };
  },
  computed: {
    typeRows() {
      const total = Number(this.stat.totalAmount || 0);
      return (this.stat.typeStats || []).map((row, index) => ({
        ...row,
        // donutOption 按 value 取扇形大小，这里用合同数量
        value: row.count || 0,
        count: row.count || 0,
        amount: Number(row.amount || 0),
        ratio: row.ratio || 0,
        amountRatio: total ? Math.round(Number(row.amount || 0) / total * 10000) / 100 : 0,
        color: REPORT_PALETTE[index % REPORT_PALETTE.length],
      }));
    },
  },
  beforeRouteLeave(to, from, next) {
    next();
  },
  mounted() {
    this.loadReport();
  },
  unmounted() {
    [this.typeChart, this.trendChart].forEach(chart => chart && chart.dispose());
  },
  methods: {
    money,
    async loadReport() {
      this.loading = true;
      try {
        const params = cleanParams({
          ...this.query,
          startTime: this.query.startTime ? this.query.startTime.slice(0, 10) : "",
          endTime: this.query.endTime ? this.query.endTime.slice(0, 10) : "",
        });
        this.stat = await contractApi.statDistribution(params) || {};
        this.$nextTick(() => this.renderCharts());
      } finally {
        this.loading = false;
      }
    },
    resetSearch() {
      this.loadReport();
    },
    resetAll() {
      const year = new Date().getFullYear();
      this.query = {startTime: `${year}-01-01`, endTime: today(), contractType: "", salesId: ""};
      this.loadReport();
    },
    renderCharts() {
      this.typeChart = this.setOption(this.typeChart, this.$refs.typeChart,
          donutOption(this.typeRows, this.stat.totalCount || 0, "合同数量"));
      this.trendChart = this.setOption(this.trendChart, this.$refs.trendChart,
          lineOption(this.stat.monthList || [], (this.stat.amountList || []).map(Number), "#1677ff"));
    },
    setOption(chart, dom, option) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption(option, true);
      return chart;
    },
    summaryMethod({columns}) {
      return columns.map((column, index) => {
        if (index === 0) {
          return "合计";
        }
        if (column.property === "count") {
          return String(this.stat.totalCount || 0);
        }
        if (column.property === "amount") {
          return money(this.stat.totalAmount);
        }
        if (column.property === "amountRatio" || column.property === "ratio") {
          return this.typeRows.length ? "100.00%" : "0.00%";
        }
        return "";
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/views/h5/crm/report/report.scss";

.lg-dot.inline {
  display: inline-block;
  margin-right: 6px;
}
</style>
